import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok';
import { TFunction } from 'i18next';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Images } from '../../../assets';
import { AtiraIcon } from '../../../components/AtiraIcon';
import { AtiraSelect } from '../../../components/AtiraSelect';
import { Button } from '../../../components/Button';
import { Drawer } from '../../../components/Drawer';
import { Flex } from '../../../components/Flex';
import { AtiraImage } from '../../../components/Image';
import { Text } from '../../../components/Text';
import i18n from '../../../i18n';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { useAppSelector } from '../../../redux/store';
import { Breakpoints } from '../../../theme/Breakpoints';
import { FontSize } from '../../../theme/FontSize';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';

enum Socials {
  INSTAGRAM = 'INSTAGRAM',
  TWITTER = 'TWITTER',
  FACEBOOK = 'FACEBOOK',
  YOUTUBE = 'YOUTUBE',
  TIKTOK = 'TIKTOK',
}

const SocialMediaIconWrapper = styled(Flex)<{
  color?: string;
  backgroundColor?: string;
  socialName?: Socials;
}>`
  color: ${({ color }) => color};
  margin: 0;
  background-color: ${({ backgroundColor }) => backgroundColor};
  width: 1.5rem;
  aspect-ratio: 1 / 1;
  align-items: center;
  justify-content: center;
  border-radius: ${Rounded.sm};

  ${({ socialName }) =>
    socialName === Socials.INSTAGRAM
      ? `
        background: -moz-linear-gradient(
          45deg,
          #f09433 0%,
          #e6683c 25%,
          #dc2743 50%,
          #cc2366 75%,
          #bc1888 100%
        );
        background: -webkit-linear-gradient(
          45deg,
          #f09433 0%,
          #e6683c 25%,
          #dc2743 50%,
          #cc2366 75%,
          #bc1888 100%
        );
        background: linear-gradient(
          45deg,
          #f09433 0%,
          #e6683c 25%,
          #dc2743 50%,
          #cc2366 75%,
          #bc1888 100%
        );
        filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#f09433', endColorstr='#bc1888',GradientType=1 );
    `
      : ''}

  svg {
    color: ${({ color }) => color};
  }
`;

const StyledList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  padding-inline-start: ${Spacing.s};

  p {
    font-size: ${FontSize.s};
  }

  li::marker {
    font-size: ${FontSize.s};
    font-weight: bold;
  }
`;

const StyledSelect = styled(AtiraSelect)`
  .ant-select-selection-item .social-media-icon-wrapper {
    display: none;
  }
`;

const CopyLinkButton = styled(Button)`
  font-size: ${FontSize.s};
  background-color: transparent;
  padding: 0;
  color: ${({ theme }) => theme.lightMain};
  padding-inline-start: calc(${Spacing.s} / 2);

  &:hover {
    color: ${({ theme }) => theme.main};
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  padding-inline-start: calc(${Spacing.s} / 2);
  color: ${({ theme }) => theme.lightMain};

  &:hover {
    color: ${({ theme }) => theme.main};
  }
`;

const ImageWrapper = styled(Flex)`
  .ant-image {
    border: ${({ theme }) => `2px solid ${theme.darkerSub}`};
    border-radius: ${Rounded.md};
    overflow: hidden;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    .ant-image {
      width: 75% !important;
    }
  }
`;

type ShareDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
};

const socialMediaLinks = [
  {
    name: Socials.INSTAGRAM,
    icon: faInstagram,
    color: '#ffffff',
  },
  {
    name: Socials.TIKTOK,
    icon: faTiktok,
    color: '#ffffff',
    backgroundColor: '#000000',
  },
];

const socialMediaOptions = (t: TFunction) =>
  socialMediaLinks.map((social) => ({
    label: (
      <Flex alignItems="center" justifyContent="space-between" gap="s">
        <Text>{t(`common.${social.name.toLowerCase()}`)}</Text>
        <SocialMediaIconWrapper
          key={social.name}
          backgroundColor={social.backgroundColor}
          color={social.color}
          socialName={social.name}
          className="social-media-icon-wrapper"
        >
          <AtiraIcon icon={social.icon} size="lg" />
        </SocialMediaIconWrapper>
      </Flex>
    ),
    value: social.name,
  }));

export const ShareDrawer: React.FC<ShareDrawerProps> = ({ open, onClose }) => {
  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const userLinks = useAppSelector(
    atiraLinkSliceSelectors.selectUserAtiraLinks,
  );

  const [selectedLinkOption, setSelectedLinkOption] = useState<{
    label: string;
    value: string;
  }>({
    label: selectedAtiraLink?.linkName || '',
    value: selectedAtiraLink?.linkName || '',
  });
  const [selectedSocialMedia, setSelectedSocialMedia] =
    useState<Socials | null>(null);

  const { t } = useTranslation();

  const userLinkUrl = `https://atrl.ink/${selectedLinkOption?.value}`;

  const copyLink = () => {
    navigator.clipboard.writeText(userLinkUrl);
    AtiraToast.success(t('shorten_url.copy.success'));
  };

  const getAtiraLinksOptions = useMemo(() => {
    return () =>
      userLinks.map((atiraLink) => ({
        value: atiraLink.linkName,
        label: atiraLink.linkName,
      }));
  }, [userLinks]);

  const getProfileURL = () => {
    switch (selectedSocialMedia) {
      case Socials.INSTAGRAM:
        return 'https://www.instagram.com/accounts/edit/';
      case Socials.TIKTOK:
        return 'https://www.tiktok.com/profile';
      default:
        return '';
    }
  };

  return (
    <Drawer
      title={t('share.drawer.title')}
      onClose={onClose}
      open={open}
      width="30rem"
    >
      <Flex gap="l" flexDirection="column">
        <Text fontWeight={'bold'}>{t('share.drawer.paragraph')}</Text>

        <Flex flexDirection="column" gap="s">
          <AtiraSelect
            title={t('share.drawer.selectLink')}
            options={getAtiraLinksOptions()}
            value={selectedLinkOption}
            onChange={(v) =>
              setSelectedLinkOption({
                value: v,
                label: v,
              })
            }
            containerStyle={{ width: '15rem' }}
            hideOptionalMark
          />
        </Flex>

        <StyledSelect
          title={t('share.drawer.selectSocialMedia')}
          options={socialMediaOptions(t)}
          containerStyle={{ width: '15rem' }}
          info={t('share.drawer.selectSocialMedia.info')}
          hideOptionalMark
          value={selectedSocialMedia}
          onChange={(v) => setSelectedSocialMedia(v)}
        />

        {selectedSocialMedia ? (
          <Flex flexDirection="column" gap="s">
            <Text fontWeight={'bold'} fontSize="s">
              {t('share.drawer.steps')}
            </Text>

            <StyledList>
              <li>
                <Text>
                  <Trans
                    i18nKey={'share.drawer.steps.1'}
                    components={[<CopyLinkButton onClick={copyLink} />]}
                  />
                </Text>
              </li>
              <li>
                <Text>
                  <Trans
                    i18nKey={'share.drawer.steps.2'}
                    components={[
                      <StyledLink href={getProfileURL()} target="_blank" />,
                    ]}
                  />
                </Text>
              </li>
              <li>
                <Text>{t('share.drawer.steps.3', { link: userLinkUrl })}</Text>
              </li>
              {selectedSocialMedia === Socials.INSTAGRAM ? (
                <ImageWrapper>
                  <AtiraImage
                    preview
                    src={
                      Images[
                        `InstagramEdit${i18n.language.toUpperCase()}` as keyof typeof Images
                      ]
                    }
                  />
                </ImageWrapper>
              ) : null}

              {selectedSocialMedia === Socials.TIKTOK ? (
                <Flex flexDirection="column" gap="s">
                  <ImageWrapper>
                    <AtiraImage
                      preview
                      src={
                        Images[
                          `TikTokEdit${i18n.language.toUpperCase()}` as keyof typeof Images
                        ]
                      }
                    />
                  </ImageWrapper>

                  <ImageWrapper>
                    <AtiraImage
                      preview
                      src={
                        Images[
                          `TikTokBio${i18n.language.toUpperCase()}` as keyof typeof Images
                        ]
                      }
                    />
                  </ImageWrapper>
                </Flex>
              ) : null}
            </StyledList>
          </Flex>
        ) : null}
      </Flex>
    </Drawer>
  );
};
